import { useMemo, FC } from 'react';
import styled from 'styled-components';
import { ButtonBase, Stack, Typography, Chip, Tooltip } from '@mui/material';
import {
  HeroConversation,
  chatActions,
  chatSelectors,
  useAppDispatch,
  useAppSelector,
} from '@vs-online-only-app/state';
import { UserAvatar, truncateMessagesCount } from '@vs-online-only-app/ui';
import { differenceInCalendarDays, format } from 'date-fns';
import useConversationShopperStatus from '../../hooks/useConversationShopperStatus';

type Props = {
  conversation: HeroConversation;
  shopperStatus?: ReturnType<typeof useConversationShopperStatus>;
  unreadMessagesCount?: number | null; // null -> chat is new
};

const StyledButtonBase = styled(ButtonBase)<{ $isSelected: boolean }>`
  display: grid;
  grid-template-columns: ${({ theme }) => theme.spacing(4)} 3fr 1fr;
  gap: ${({ theme }) => theme.spacing(1.5)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding: ${({ theme }) => theme.spacing(2, 1)};
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.palette.background.default : 'transparent'};
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.palette.background.default};
  }
`;

const MessagePreview = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: left;
  word-break: break-word;
`;

const ConversationItemCard: FC<Props> = ({
  conversation,
  shopperStatus,
  unreadMessagesCount,
}) => {
  const dispatch = useAppDispatch();

  const isArchived = useMemo(
    () => Boolean(conversation.archiveAttributes),
    [conversation.archiveAttributes],
  );

  const selectedConversation = useAppSelector(
    chatSelectors.selectSelectedConversation,
  );
  const isSelected = useMemo(
    () => selectedConversation?.id === conversation.id,
    [selectedConversation, conversation.id],
  );

  const lastReceivedMessageTimestamp = useMemo(() => {
    const lastReceivedMessageDate = new Date(
      conversation.archiveAttributes
        ? conversation.archiveAttributes.archivedAt
        : conversation.lastReceivedMessage,
    );
    const daysDiff = differenceInCalendarDays(
      Date.now(),
      lastReceivedMessageDate,
    );

    return format(
      lastReceivedMessageDate,
      daysDiff < 1 ? 'HH:mm' : 'dd/MM/yyyy',
    );
  }, [conversation]);

  const visibleUnreadMessagesCount = truncateMessagesCount(unreadMessagesCount);

  return (
    <StyledButtonBase
      $isSelected={isSelected}
      onClick={() => {
        dispatch(chatActions.setSelectedConversation(conversation));
      }}
    >
      <UserAvatar
        fallbackCharacter={conversation.conversationStarter.firstName
          .charAt(0)
          .toUpperCase()}
        hasStatusIndicator={!isArchived}
        isOnline={!shopperStatus?.isAway}
        color={isArchived ? undefined : '#E95D11'} // Bambuser orange
      />
      <Stack>
        <Typography
          fontWeight={unreadMessagesCount ? 'bold' : 'normal'}
          textAlign="left"
          variant="body2"
        >
          {conversation.conversationStarter.firstName}
        </Typography>
        <MessagePreview
          color={unreadMessagesCount ? 'textPrimary' : 'textSecondary'}
          variant="body2"
        >
          {conversation.lastMessageContent}
        </MessagePreview>
        {isArchived && conversation.archiveAttributes?.salesCount ? (
          <Tooltip
            title={`${conversation.archiveAttributes.salesAmount} spent`}
          >
            <Chip
              label={conversation.archiveAttributes.salesAmount}
              size="small"
              color="success"
              sx={{ mt: 0.5, maxWidth: '100px' }}
            />
          </Tooltip>
        ) : null}
      </Stack>
      <Stack gap={0.5} height="100%">
        <Typography variant="caption" color="textSecondary" textAlign="right">
          {lastReceivedMessageTimestamp}
        </Typography>
        <Stack direction="row" gap={0.5} justifyContent="flex-end">
          {/* null -> chat is new and was not opened yet */}
          {unreadMessagesCount === null && (
            <Chip label="New" color="secondary" size="small" />
          )}
          {Boolean(unreadMessagesCount) && (
            <Chip
              label={visibleUnreadMessagesCount}
              size="small"
              color="primary"
            />
          )}
        </Stack>
      </Stack>
    </StyledButtonBase>
  );
};

export default ConversationItemCard;
